import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { DeliveryConfig, DeliveryZone } from "../../utils/deliveryQuote";
import { API_URL, DELIVERY_ZONES_FILE_URL } from "../../config";

export type DeliveryZonesResponse = {
  zones: DeliveryZone[];
  config: DeliveryConfig;
};

/**
 * The singleton settings row. It is edited through `PUT /delivery-zones/{id}` like any other row —
 * the Lambda skips municipality validation for exactly this id — so the admin screen needs to name
 * it, and naming it once here beats a string literal in a component.
 */
export const DELIVERY_CONFIG_ID = "__config__";

export const deliveryZonesApi = createApi({
  reducerPath: "deliveryZonesApi",
  tagTypes: ["DeliveryZone"],
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /**
     * The published file first, the API second — the arrangement `getItemCards` already uses.
     *
     * A `queryFn` rather than a `query` because the choice depends on the *outcome* of the first
     * attempt, which `query` cannot express. The fallback is what makes the file an optimisation
     * rather than a cutover: before the first publish, and any time the object is missing, the
     * storefront reads the API and works.
     */
    getDeliveryZones: builder.query<DeliveryZonesResponse, void>({
      async queryFn(_arg, _api, _extra, baseQuery) {
        if (DELIVERY_ZONES_FILE_URL) {
          try {
            const response = await fetch(DELIVERY_ZONES_FILE_URL);
            if (response.ok) return { data: (await response.json()) as DeliveryZonesResponse };
          } catch {
            // Network error, CORS, a 404 before the first publish — all the same answer: ask the
            // API. Deliberately not surfaced as an error; the zones still load.
          }
        }

        const result = await baseQuery("/delivery-zones");
        if (result.error) return { error: result.error };
        return { data: result.data as DeliveryZonesResponse };
      },
      providesTags: ["DeliveryZone"],
    }),

    /**
     * The same data, read straight from the API — never from the published file.
     *
     * The admin edits the table and then has to see what it now holds. `delivery-zones.json` is a
     * CloudFront object whose invalidation is asynchronous, so the storefront query above can
     * legitimately serve the copy from before the save that just succeeded. The grid reading its
     * own writes matters more here than an edge hit does, and this is the same split
     * `useGetItemsQuery` already has against `getItemCards`.
     */
    getDeliveryZonesAdmin: builder.query<DeliveryZonesResponse, void>({
      query: () => "/delivery-zones",
      providesTags: ["DeliveryZone"],
    }),

    createDeliveryZone: builder.mutation<DeliveryZone, DeliveryZone>({
      query: (zone) => ({ url: "/delivery-zones", method: "POST", body: zone }),
      invalidatesTags: ["DeliveryZone"],
    }),

    updateDeliveryZone: builder.mutation<
      DeliveryZone,
      { id: string; patch: Partial<DeliveryZone> | Partial<DeliveryConfig> }
    >({
      query: ({ id, patch }) => ({ url: `/delivery-zones/${id}`, method: "PUT", body: patch }),
      invalidatesTags: ["DeliveryZone"],
    }),

    deleteDeliveryZone: builder.mutation<{ id: string }, string>({
      query: (id) => ({ url: `/delivery-zones/${id}`, method: "DELETE" }),
      invalidatesTags: ["DeliveryZone"],
    }),

    /** Writes the 53 supplied municipalities, skipping any that already exist. Safe to re-run. */
    seedDeliveryZones: builder.mutation<
      { created: number; skipped: number; skipped_ids: string[] },
      void
    >({
      query: () => ({ url: "/delivery-zones/seed", method: "POST" }),
      invalidatesTags: ["DeliveryZone"],
    }),
  }),
});

export const {
  useGetDeliveryZonesQuery,
  useGetDeliveryZonesAdminQuery,
  useCreateDeliveryZoneMutation,
  useUpdateDeliveryZoneMutation,
  useDeleteDeliveryZoneMutation,
  useSeedDeliveryZonesMutation,
} = deliveryZonesApi;
